import axios from 'axios'
import { User, UserRole, UserStatus } from '../../domain/entities/User'
import { GetUsersFilters, GetUsersResponse, UserRepository } from '../../domain/repositories/UserRepository'
import { userApiDatasource, UserApiResponse } from '../datasources/UserApiDatasource'

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined
    return data?.message || data?.error || fallback
  }

  return err instanceof Error ? err.message : fallback
}

const toDomainUser = (u: UserApiResponse): User => ({
  id: u.id,
  keycloakId: u.keycloak_id,
  name: u.name,
  role: u.role as UserRole,
  phone: u.phone,
  email: u.email,
  status: u.status as UserStatus,
  createdAt: u.created_at,
  updatedAt: u.updated_at,
})

export class UserRepositoryImpl implements UserRepository {
  public async getUsers(filters: GetUsersFilters): Promise<GetUsersResponse> {
    try {
      const res = await userApiDatasource.getUsers({
        skip: (filters.page - 1) * filters.limit,
        limit: filters.limit,
        role: filters.role || undefined,
        search: filters.search || undefined,
      })

      return {
        users: res.data.map(toDomainUser),
        totalItems: res.total,
        totalPages: Math.max(1, Math.ceil(res.total / filters.limit)),
      }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load users.'))
    }
  }

  public async getUserById(id: string): Promise<User | null> {
    try {
      const user = await userApiDatasource.getUserById(id)
      return toDomainUser(user)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      throw new Error(extractErrorMessage(err, 'Failed to load user.'))
    }
  }

  public async createUser(
    user: Omit<User, 'id' | 'keycloakId' | 'status' | 'createdAt' | 'updatedAt'> & { password?: string },
  ): Promise<User> {
    try {
      const created = await userApiDatasource.registerUser({
        name: user.name,
        phone: user.phone,
        email: user.email || '',
        role: user.role,
        password: user.password,
      })
      return toDomainUser(created)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to create user.'))
    }
  }

  public async updateUser(
    id: string,
    user: Partial<Omit<User, 'id' | 'keycloakId' | 'createdAt' | 'updatedAt'>>,
  ): Promise<User> {
    try {
      // The backend's update endpoint has no email field — email can only be set at registration.
      const updated = await userApiDatasource.updateUser(id, {
        name: user.name,
        phone: user.phone,
        role: user.role,
        status: user.status,
      })
      return toDomainUser(updated)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to update user.'))
    }
  }

  public async toggleUserStatus(id: string, status: UserStatus): Promise<User> {
    try {
      const updated = await userApiDatasource.updateUser(id, { status })
      return toDomainUser(updated)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to update user status.'))
    }
  }
}
export const userRepository = new UserRepositoryImpl()
